import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import PurchaseOrderStatusPill, { PurchaseOrderApprovalTag } from './PurchaseOrderStatusPill';
import PurchaseOrderFormModal, { type PurchaseOrderEditTarget } from './PurchaseOrderFormModal';
import PurchaseOrderDetailModal from './PurchaseOrderDetailModal';
import PurchaseOrderReceiveModal from './PurchaseOrderReceiveModal';
import {
  canReceivePurchaseOrder,
  cancelPurchaseOrder,
  fetchPurchaseOrder,
  fetchPurchaseOrders,
  purchaseOrderMatchesQuery,
  reopenPurchaseOrder,
  type PurchaseOrderItemRow,
  type PurchaseOrderListResponse,
  type PurchaseOrderRow,
} from '../../api/purchaseOrders';
import './PurchaseOrders.css';

/**
 * The Purchase Order tab: the Ordering and Receiving cards, a search box, the
 * Generate Purchase Order button, and the three tables.
 *
 * The cards are computed server-side from the same rows the tables render, so the
 * counts always agree with what is on screen. The search box narrows all three
 * tables at once and matches the PO number, vendor, item description,
 * status, and the people and dates shown in the columns.
 *
 * The Receiving table is the active queue: everything still owed to us, which is the
 * Open and partially received orders and therefore every row that can read Overdue.
 * Keeping partials here is what lets a short shipment be topped up and an overdue
 * order be chased from one place. A PO moves to the Order Log once it is fully
 * received, or to the Cancelled table if it is cancelled.
 */

function money(value: number): string {
  return `$${(Number.isFinite(value) ? value : 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Whole days past the expected delivery date, or a dash when not applicable. */
function daysOverdue(po: PurchaseOrderRow): string {
  if (!po.is_overdue || !po.expected_delivery_date) return '—';
  const [y, m, d] = po.expected_delivery_date.split('-').map(Number);
  const due = Date.UTC(y, m - 1, d);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((today - due) / 86_400_000);
  return days > 0 ? String(days) : '—';
}

/** One line's description, or the first plus a count of the rest. */
function itemSummary(po: PurchaseOrderRow): string {
  if (!po.first_description) return '—';
  const extra = po.item_count - 1;
  return extra > 0 ? `${po.first_description} +${extra} more` : po.first_description;
}

function variance(po: PurchaseOrderRow): string {
  if (po.date_received == null) return '—';
  if (po.qty_variance === 0) return '0';
  return po.qty_variance > 0 ? `+${po.qty_variance}` : String(po.qty_variance);
}

interface CardMetric {
  label: string;
  value: string | number;
  emphasize?: boolean;
}

function SummaryCard({ title, metrics }: { title: string; metrics: CardMetric[] }) {
  return (
    <section className="po-card">
      <h3 className="po-card-title">{title}</h3>
      <dl className="po-card-metrics">
        {metrics.map((m) => (
          <div key={m.label} className={`po-card-metric${m.emphasize ? ' po-card-metric--emphasis' : ''}`}>
            <dt>{m.label}</dt>
            <dd>{m.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

interface ReceiveTarget {
  po: PurchaseOrderRow;
  items: PurchaseOrderItemRow[];
  /** `stock` opens on the lines already received but not yet in inventory. */
  startAt: 'receive' | 'stock';
}

const EMPTY: PurchaseOrderListResponse = {
  receiving: [],
  orderLog: [],
  cancelled: [],
  summary: {
    ordering: { total: 0, open: 0, partiallyReceived: 0, fullyReceived: 0, cancelled: 0, overdue: 0, totalSpend: 0 },
    receiving: {
      totalReceived: 0,
      complete: 0,
      partial: 0,
      rejected: 0,
      qtyVariance: 0,
      failedInspections: 0,
      pendingInspections: 0,
      awaitingInventory: 0,
    },
  },
};

const PurchaseOrderTab: React.FC = () => {
  const [data, setData] = useState<PurchaseOrderListResponse>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PurchaseOrderEditTarget | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  // Bumped after an edit so the detail modal behind it remounts and re-reads the PO.
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const [receiveTarget, setReceiveTarget] = useState<ReceiveTarget | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PurchaseOrderRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await fetchPurchaseOrders());
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load purchase orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const receiving = useMemo(() => data.receiving.filter((po) => purchaseOrderMatchesQuery(po, query)), [data, query]);
  const orderLog = useMemo(() => data.orderLog.filter((po) => purchaseOrderMatchesQuery(po, query)), [data, query]);
  const cancelled = useMemo(() => data.cancelled.filter((po) => purchaseOrderMatchesQuery(po, query)), [data, query]);

  const orderingMetrics: CardMetric[] = [
    { label: 'Total Purchase Orders Logged', value: data.summary.ordering.total },
    { label: 'Open POs', value: data.summary.ordering.open },
    { label: 'Partially Received POs', value: data.summary.ordering.partiallyReceived },
    { label: 'Fully Received POs', value: data.summary.ordering.fullyReceived },
    { label: 'Cancelled POs', value: data.summary.ordering.cancelled },
    { label: 'Overdue POs (needs follow-up)', value: data.summary.ordering.overdue },
    { label: 'Total PO Spend', value: money(data.summary.ordering.totalSpend), emphasize: true },
  ];

  const receivingMetrics: CardMetric[] = [
    { label: 'Total Shipments Received', value: data.summary.receiving.totalReceived },
    { label: 'Shipments Complete', value: data.summary.receiving.complete },
    { label: 'Shipments Partial', value: data.summary.receiving.partial },
    { label: 'Shipments Rejected / Return Needed', value: data.summary.receiving.rejected },
    { label: 'Shipments with Qty Variance', value: data.summary.receiving.qtyVariance },
    { label: 'Failed Inspections', value: data.summary.receiving.failedInspections },
    { label: 'Pending Inspections', value: data.summary.receiving.pendingInspections },
    { label: 'Awaiting Inventory', value: data.summary.receiving.awaitingInventory, emphasize: true },
  ];

  /** The action buttons need the PO's items, which the list response leaves out. */
  const openReceive = async (po: PurchaseOrderRow, startAt: ReceiveTarget['startAt']) => {
    try {
      const detail = await fetchPurchaseOrder(po.id);
      setReceiveTarget({ po: detail.purchaseOrder, items: detail.items, startAt });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open the purchase order.');
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    const reason = cancelReason.trim();
    if (!reason) {
      setCancelError('A reason is required to cancel a purchase order.');
      return;
    }
    setCancelBusy(true);
    try {
      await cancelPurchaseOrder(cancelTarget.id, reason);
      setCancelTarget(null);
      setCancelReason('');
      setCancelError('');
      setDetailId(null);
      await load();
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel the purchase order.');
    } finally {
      setCancelBusy(false);
    }
  };

  const handleReopen = async (po: PurchaseOrderRow) => {
    try {
      await reopenPurchaseOrder(po.id);
      setDetailId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reopen the purchase order.');
    }
  };

  /** Opens the stock step for received goods that are not in inventory yet. */
  const stockButton = (po: PurchaseOrderRow, primary: boolean) => (
    <button
      type="button"
      className={`${primary ? 'po-primary-btn' : 'po-secondary-btn'} po-row-btn`}
      onClick={(e) => {
        e.stopPropagation();
        void openReceive(po, 'stock');
      }}
    >
      Add to inventory
    </button>
  );

  /**
   * Received, Add to inventory (while received goods are waiting) and Cancel for a
   * Receiving row. An order that is not approved shows that in place of the two
   * receiving buttons. stopPropagation keeps the click off the row itself, which
   * opens the detail modal.
   */
  const rowActions = (po: PurchaseOrderRow) => (
    <div className="po-row-actions">
      {canReceivePurchaseOrder(po) ? (
        <>
          <button
            type="button"
            className="po-primary-btn po-row-btn"
            onClick={(e) => {
              e.stopPropagation();
              void openReceive(po, 'receive');
            }}
          >
            Received
          </button>
          {po.awaiting_inventory_count > 0 && stockButton(po, false)}
        </>
      ) : (
        <PurchaseOrderApprovalTag approval={po.approval_status} />
      )}
      <button
        type="button"
        className="po-danger-btn po-row-btn"
        onClick={(e) => {
          e.stopPropagation();
          setCancelTarget(po);
          setCancelReason('');
          setCancelError('');
        }}
      >
        Cancel
      </button>
    </div>
  );

  return (
    <>
      <div className="po-cards">
        <SummaryCard title="Ordering" metrics={orderingMetrics} />
        <SummaryCard title="Receiving" metrics={receivingMetrics} />
      </div>

      <div className="po-toolbar">
        <input
          type="text"
          className="page-toolbar-search po-search"
          placeholder="Search PO number, vendor, item, status..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search purchase orders"
        />
        <div className="po-toolbar-actions">
          <button type="button" className="po-primary-btn" onClick={() => setFormOpen(true)}>
            Generate Purchase Order
          </button>
        </div>
      </div>

      {error ? <p className="create-order-error" role="alert">{error}</p> : null}

      <div className="po-table-section">
        <h3 className="inventory-section-title">Receiving Log</h3>
        <p className="po-table-caption">
          Purchase orders still owed to us — open and partially received. Overdue rows are past their expected delivery
          date.
        </p>
        <div className="po-table-wrapper">
          <table className="po-table po-table--list">
            <thead>
              <tr>
                <th>PO #</th>
                <th>Vendor</th>
                <th>Item / Description</th>
                <th>Expected Delivery</th>
                <th className="po-col-num">Total Cost</th>
                <th>ETA</th>
                <th className="po-col-num">Days Overdue</th>
                <th>Date Received</th>
                <th>Tracking Number</th>
                <th className="po-col-num">Qty Ordered</th>
                <th className="po-col-num">Qty Received</th>
                <th className="po-col-num">Variance</th>
                <th>Condition</th>
                <th>Inspection</th>
                <th>Received By</th>
                <th>Status</th>
                <th className="po-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {receiving.map((po) => (
                <tr key={po.id} className="po-row" onClick={() => setDetailId(po.id)}>
                  <td data-label="PO #">{po.po_number}</td>
                  <td data-label="Vendor">{po.vendor_name}</td>
                  <td data-label="Item / Description">{itemSummary(po)}</td>
                  <td data-label="Expected Delivery">{po.expected_delivery_date || '—'}</td>
                  <td data-label="Total Cost" className="po-col-num">{money(po.total)}</td>
                  <td data-label="ETA">{po.eta || '—'}</td>
                  <td data-label="Days Overdue" className="po-col-num">{daysOverdue(po)}</td>
                  <td data-label="Date Received">{po.date_received || '—'}</td>
                  <td data-label="Tracking Number">{po.carrier_tracking || '—'}</td>
                  <td data-label="Qty Ordered" className="po-col-num">{po.qty_ordered}</td>
                  <td data-label="Qty Received" className="po-col-num">{po.qty_received}</td>
                  <td data-label="Variance" className="po-col-num">{variance(po)}</td>
                  <td data-label="Condition">{po.condition_on_arrival || '—'}</td>
                  <td data-label="Inspection">{po.inspection_result || '—'}</td>
                  <td data-label="Received By">{po.received_by || '—'}</td>
                  <td data-label="Status"><PurchaseOrderStatusPill status={po.display_status} /></td>
                  <td data-label="Actions" className="po-col-actions">
                    {rowActions(po)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && receiving.length === 0 && (
          <p className="page-subtitle">
            {query ? 'No purchase orders awaiting delivery match your search.' : 'Nothing is awaiting delivery.'}
          </p>
        )}
      </div>

      <div className="po-table-section">
        <h3 className="inventory-section-title">Order Log</h3>
        <p className="po-table-caption">Purchase orders that have been fully received.</p>
        <div className="po-table-wrapper">
          <table className="po-table po-table--list">
            <thead>
              <tr>
                <th>PO #</th>
                <th>Vendor</th>
                <th>Item / Description</th>
                <th className="po-col-num">Qty Ordered</th>
                <th className="po-col-num">Qty Received</th>
                <th className="po-col-num">Total Cost</th>
                <th className="po-col-num">Freight Charges</th>
                <th className="po-col-num">Processing Fee</th>
                <th>Order Date</th>
                <th>Expected Delivery</th>
                <th>Date Received</th>
                <th>Ordered By</th>
                <th>Approval Status</th>
                <th>Status</th>
                <th className="po-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orderLog.map((po) => (
                <tr key={po.id} className="po-row" onClick={() => setDetailId(po.id)}>
                  <td data-label="PO #">{po.po_number}</td>
                  <td data-label="Vendor">{po.vendor_name}</td>
                  <td data-label="Item / Description">{itemSummary(po)}</td>
                  <td data-label="Qty Ordered" className="po-col-num">{po.qty_ordered}</td>
                  <td data-label="Qty Received" className="po-col-num">{po.qty_received}</td>
                  <td data-label="Total Cost" className="po-col-num">{money(po.total)}</td>
                  <td data-label="Freight Charges" className="po-col-num">{money(po.shipping)}</td>
                  <td data-label="Processing Fee" className="po-col-num">{money(po.processing_fee)}</td>
                  <td data-label="Order Date">{po.date_issued || '—'}</td>
                  <td data-label="Expected Delivery">{po.expected_delivery_date || '—'}</td>
                  <td data-label="Date Received">{po.date_received || '—'}</td>
                  <td data-label="Ordered By">{po.requested_by || po.created_by || '—'}</td>
                  <td data-label="Approval Status">{po.approval_status}</td>
                  <td data-label="Status"><PurchaseOrderStatusPill status={po.display_status} /></td>
                  <td data-label="Actions" className="po-col-actions">
                    {po.awaiting_inventory_count === 0 ? (
                      '—'
                    ) : canReceivePurchaseOrder(po) ? (
                      stockButton(po, true)
                    ) : (
                      <PurchaseOrderApprovalTag approval={po.approval_status} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && orderLog.length === 0 && (
          <p className="page-subtitle">
            {query
              ? 'No fully received purchase orders match your search.'
              : 'No purchase orders have been fully received yet.'}
          </p>
        )}
      </div>

      <div className="po-table-section">
        <h3 className="inventory-section-title">Cancelled Purchase Orders</h3>
        <div className="po-table-wrapper">
          <table className="po-table po-table--list">
            <thead>
              <tr>
                <th>PO #</th>
                <th>Vendor</th>
                <th>Item / Description</th>
                <th className="po-col-num">Qty Ordered</th>
                <th className="po-col-num">Total Cost</th>
                <th>Order Date</th>
                <th>Expected Delivery</th>
                <th>Reason</th>
                <th>Status</th>
                <th className="po-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cancelled.map((po) => (
                <tr key={po.id} className="po-row" onClick={() => setDetailId(po.id)}>
                  <td data-label="PO #">{po.po_number}</td>
                  <td data-label="Vendor">{po.vendor_name}</td>
                  <td data-label="Item / Description">{itemSummary(po)}</td>
                  <td data-label="Qty Ordered" className="po-col-num">{po.qty_ordered}</td>
                  <td data-label="Total Cost" className="po-col-num">{money(po.total)}</td>
                  <td data-label="Order Date">{po.date_issued || '—'}</td>
                  <td data-label="Expected Delivery">{po.expected_delivery_date || '—'}</td>
                  <td data-label="Reason">{po.cancel_reason || '—'}</td>
                  <td data-label="Status"><PurchaseOrderStatusPill status={po.display_status} /></td>
                  <td data-label="Actions" className="po-col-actions">
                    <button
                      type="button"
                      className="po-secondary-btn po-row-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleReopen(po);
                      }}
                    >
                      Reopen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && cancelled.length === 0 && (
          <p className="page-subtitle">
            {query ? 'No cancelled purchase orders match your search.' : 'No purchase orders have been cancelled.'}
          </p>
        )}
      </div>

      <PurchaseOrderFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={(po) => {
          setFormOpen(false);
          void load();
          setDetailId(po.id);
        }}
        onShowExisting={(id) => {
          setFormOpen(false);
          setDetailId(id);
        }}
      />

      {editTarget && (
        <PurchaseOrderFormModal
          isOpen
          editing={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            void load();
            setDetailReloadKey((n) => n + 1);
          }}
          onShowExisting={(id) => {
            setEditTarget(null);
            setDetailId(id);
          }}
        />
      )}

      {detailId != null && (
        <PurchaseOrderDetailModal
          key={`${detailId}-${detailReloadKey}`}
          purchaseOrderId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => void load()}
          onRequestReceive={(po, items) => setReceiveTarget({ po, items, startAt: 'receive' })}
          onRequestStock={(po, items) => setReceiveTarget({ po, items, startAt: 'stock' })}
          onRequestCancel={(po) => {
            setCancelTarget(po);
            setCancelReason('');
            setCancelError('');
          }}
          onRequestReopen={(po) => void handleReopen(po)}
          onRequestEdit={(po, items) => setEditTarget({ purchaseOrder: po, items })}
        />
      )}

      {receiveTarget && (
        <PurchaseOrderReceiveModal
          key={`${receiveTarget.po.id}-${receiveTarget.startAt}`}
          isOpen
          purchaseOrder={receiveTarget.po}
          items={receiveTarget.items}
          startAt={receiveTarget.startAt}
          onClose={() => setReceiveTarget(null)}
          onChanged={() => void load()}
        />
      )}

      <Modal
        isOpen={cancelTarget != null}
        onClose={() => {
          if (!cancelBusy) setCancelTarget(null);
        }}
        title="Cancel this purchase order"
        narrow
        elevated
      >
        <div className="modal-body">
          <p className="attachment-void-explainer">
            {cancelTarget ? (
              <>
                <strong>{cancelTarget.po_number}</strong> moves to the Cancelled table with your reason attached. It can
                be reopened later.
              </>
            ) : null}
          </p>
          <div className="form-group">
            <label className="form-label" htmlFor="po-cancel-reason">Reason for cancelling</label>
            <textarea
              id="po-cancel-reason"
              className="form-textarea"
              rows={3}
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
                setCancelError('');
              }}
              placeholder="e.g. Vendor cannot meet the delivery date"
              disabled={cancelBusy}
            />
          </div>
          {cancelError ? <p className="create-order-error" role="alert">{cancelError}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={cancelBusy} onClick={() => setCancelTarget(null)}>
            Keep it
          </button>
          <button type="button" className="po-danger-btn" disabled={cancelBusy} onClick={() => void confirmCancel()}>
            {cancelBusy ? 'Cancelling…' : 'Cancel purchase order'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default PurchaseOrderTab;
